import { useContext } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import {
    ArrowBackIcon,
    colors,
    DeleteOutlineIcon,
    ExpandMoreIcon,
    TaskCancelIcon
} from "@dotnet/telematics-design-system";

import DeleteSelectedItems from "components/Actions/dialogs/components/DeleteSelectedItems";
import CancelSelectedTasks from "components/Actions/dialogs/Tasks/CancelSelectedTasks";
import { TASK_ACTIONS } from "components/Actions/types";
import ButtonDefault from "components/DesignComponents/ButtonDefault";
import ListChoice from "components/DesignComponents/ListChoice";
import Menu, {
    centeredAnchorOrigin,
    centeredTransformOrigin
} from "components/DesignComponents/Menu";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";
import TableContext from "context/table/tableContext";
import { useMediaQueries } from "shared";

import { useActions } from "./hooks";
import { ActionsProps } from "./types";

const Actions = ({ toggleActiveHead }: ActionsProps) => {
    const { blue700, gray100, textDisabled, white } = colors;

    const { selectedRows } = useContext(TableContext);

    const { t } = useTranslation();
    const { toMd } = useMediaQueries();

    const {
        action,
        anchorEl,
        buttonText,
        isCancelTasksAction,
        isDisabled,
        isOpenDialog,
        mainTableName,
        handleCloseMenu,
        handleOpenMenu,
        noPermissionTooltipTitle,
        closeDialog,
        openDialog,
        resetView
    } = useActions(toggleActiveHead);

    const getIconLeft = isCancelTasksAction ? (
        <TaskCancelIcon />
    ) : (
        <DeleteOutlineIcon />
    );

    const renderContent = () => {
        if (action && selectedRows.length > 0) {
            return (
                <>
                    {toMd ? (
                        <>
                            <ButtonDefault onClick={handleOpenMenu}>
                                {t("Button##actions")}
                                <ExpandMoreIcon
                                    css={css({
                                        marginLeft: "4px"
                                    })}
                                />
                            </ButtonDefault>

                            <Menu
                                open={Boolean(anchorEl)}
                                onClose={handleCloseMenu}
                                anchorEl={anchorEl}
                                slotProps={{
                                    list: { disableListWrap: true }
                                }}
                                anchorOrigin={centeredAnchorOrigin}
                                transformOrigin={centeredTransformOrigin}
                                css={css({
                                    "& .MuiMenu-paper": {
                                        maxHeight: "337px",
                                        minWidth: "211px"
                                    }
                                })}
                            >
                                <ListChoice
                                    title={t("Button##default view")}
                                    iconLeft={<ArrowBackIcon />}
                                    onClick={resetView}
                                    css={css({
                                        background: white,
                                        color: blue700
                                    })}
                                />

                                <ListChoice
                                    title={buttonText}
                                    iconLeft={getIconLeft}
                                    disabled={isDisabled}
                                    tooltipText={noPermissionTooltipTitle()}
                                    onClick={() => openDialog(action)}
                                />
                            </Menu>
                        </>
                    ) : (
                        <TooltipGeneral
                            placement={TOOLTIP_PLACEMENT.Top}
                            title={noPermissionTooltipTitle()}
                        >
                            <ButtonDefault
                                onClick={() =>
                                    !isDisabled && openDialog(action)
                                }
                                css={css({
                                    ...(isDisabled && {
                                        color: `${textDisabled} !important`,
                                        background: `${gray100} !important`,
                                        cursor: "default !important",

                                        "&:hover, &:active": {
                                            background: `${gray100} !important`
                                        }
                                    })
                                })}
                            >
                                {buttonText}
                            </ButtonDefault>
                        </TooltipGeneral>
                    )}

                    <DeleteSelectedItems
                        isOpen={
                            isCancelTasksAction
                                ? false
                                : isOpenDialog[
                                      action as keyof typeof isOpenDialog
                                  ]
                        }
                        close={closeDialog}
                        action={action}
                    />

                    <CancelSelectedTasks
                        mainTableName={mainTableName}
                        isOpen={isOpenDialog[TASK_ACTIONS.CancelTasks]}
                        close={closeDialog}
                    />
                </>
            );
        }

        return toMd ? (
            <ButtonDefault onClick={resetView}>
                {t("Button##default view")}
            </ButtonDefault>
        ) : null;
    };

    return renderContent();
};

export default Actions;
